import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Avatar,
  Chip,
  Tabs,
  Tab,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Link,
  Tooltip,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputLabel,
} from "@mui/material";
import ArrowBackIosNew from "@mui/icons-material/ArrowBackIosNew";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import PhoneOutlined from "@mui/icons-material/PhoneOutlined";
import LocationOnOutlined from "@mui/icons-material/LocationOnOutlined";
import CalendarTodayOutlined from "@mui/icons-material/CalendarTodayOutlined";
import SarIcon from "../../icons/SarIcon";
import TrendingUp from "@mui/icons-material/TrendingUp";
import Apartment from "@mui/icons-material/Apartment";
import AccountBalanceWallet from "@mui/icons-material/AccountBalanceWallet";
import PauseCircleOutline from "@mui/icons-material/PauseCircleOutline";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  useGetAdminInvestorDetailsQuery,
  useGetAdminOwnerDetailsQuery,
  useChangeUserStatusMutation,
  useVerifyKYCMutation,
  useGetVerificationDetailQuery,
} from "../../../redux/api/authApi";
import { VerificationReviewDialog } from "../AdminVerifications/AdminVerifications";
import { dashboardShell } from "../../../theme/dashboardShell";

const shellAdmin = dashboardShell.admin;

function formatMoney(amount, currency = "SAR") {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "—";
  const c = currency && String(currency).trim() ? String(currency).trim() : "SAR";
  return `${c} ${num.toLocaleString()}`;
}

function formatJoined(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { dateStyle: "medium" });
}

function roleLabel(role) {
  if (role === "investor") return "Investor";
  if (role === "assetOwner") return "Asset Owner";
  return role || "—";
}

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`user-tabpanel-${index}`}>
      {value === index ? <Box sx={{ pt: 2.5 }}>{children}</Box> : null}
    </div>
  );
}

const StatCard = ({ icon, title, value }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: 2,
      border: (theme) => `1px solid ${theme.palette.border.light}`,
      boxShadow: (theme) => theme.palette.shadow.statCard,
      height: "100%",
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 1.5,
          bgcolor: "background.subtle",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: "text.muted", fontWeight: 600, display: "block" }}>
          {title}
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: "text.primary", mt: 0.25 }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const STATUS_CHIP_KEY = {
  Verified: "success",
  Pending: "pending",
  Rejected: "suspended",
  Active: "info",
  Inactive: "neutral",
  Suspended: "suspended",
};

const KYC_STATUS_OPTIONS = ["Pending", "Verified", "Rejected"];

function TruncatedCell({ value }) {
  const text = value == null || value === "" ? "—" : String(value);
  if (text === "—" || text.length <= 10) {
    return <span>{text}</span>;
  }
  return (
    <Tooltip title={text} arrow placement="top">
      <span style={{ cursor: "help" }}>{`${text.slice(0, 10)}…`}</span>
    </Tooltip>
  );
}

function statusBadge(label) {
  const key = STATUS_CHIP_KEY[label] || "pending";
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        fontSize: 11,
        fontWeight: 600,
        height: 24,
        bgcolor: (theme) => theme.palette.chips[key].bg,
        color: (theme) => theme.palette.chips[key].text,
        borderRadius: 999,
      }}
    />
  );
}

/** `id` query param for GET /api/admin/verification/detail (per verification type). */
function verificationDetailQueryId(verificationType, profile) {
  if (!profile || typeof profile !== "object") return "";
  const pick = (v) => (v != null && String(v).trim() !== "" ? String(v).trim() : "");
  const t = String(verificationType || "").toUpperCase();
  if (t === "NAFATH") return pick(profile.nafathId) || pick(profile._id);
  if (t === "VARA") return pick(profile.varaId);
  if (t === "REGA") {
    return (
      pick(profile.regaId) ||
      pick(profile.regaVerificationId) ||
      pick(profile.regaSubmissionId) ||
      pick(profile.reGaCertificateId)
    );
  }
  if (t === "TAUFEEQ") {
    return (
      pick(profile.taufeeqId) ||
      pick(profile.taufeeqVerificationId) ||
      pick(profile.taufeeqAssetId) ||
      pick(profile.valuationReportAssetId)
    );
  }
  return "";
}

export default function AdminUserDetail() {
  const { userId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isInvestor = pathname.includes("/users/investor/");

  const [tab, setTab] = React.useState(0);
  const documentsTabIndex = isInvestor ? 1 : 0;

  React.useEffect(() => {
    setTab(0);
  }, [userId, isInvestor]);

  const [invPage, setInvPage] = React.useState(0);
  const [invRowsPerPage, setInvRowsPerPage] = React.useState(5);
  const [typeFilter, setTypeFilter] = React.useState("all");
  const [statusFilter, setStatusFilter] = React.useState("all");

  const investorArg = React.useMemo(
    () => ({
      userId,
      page: invPage + 1,
      limit: invRowsPerPage,
    }),
    [userId, invPage, invRowsPerPage],
  );

  const {
    data: investorRes,
    isLoading: investorLoading,
    isError: investorError,
    refetch: refetchInvestor,
  } = useGetAdminInvestorDetailsQuery(investorArg, { skip: !isInvestor || !userId });

  const {
    data: ownerRes,
    isLoading: ownerLoading,
    isError: ownerError,
    refetch: refetchOwner,
  } = useGetAdminOwnerDetailsQuery(userId, { skip: isInvestor || !userId });

  const [changeUserStatus, { isLoading: statusLoading }] = useChangeUserStatusMutation();
  const [verifyKYC, { isLoading: kycSaving }] = useVerifyKYCMutation();

  const [kycDialogOpen, setKycDialogOpen] = React.useState(false);
  const [kycStatusDraft, setKycStatusDraft] = React.useState("Pending");

  const [reviewTarget, setReviewTarget] = React.useState(null);

  const {
    data: verificationDetailRes,
    isLoading: verificationDetailLoading,
    isFetching: verificationDetailFetching,
    isError: verificationDetailIsError,
    error: verificationDetailErr,
  } = useGetVerificationDetailQuery(
    { type: reviewTarget?.type ?? "", id: reviewTarget?.id ?? "" },
    { skip: !reviewTarget?.id || !reviewTarget?.type },
  );

  const verificationDetailPayload = verificationDetailRes?.data;
  const verificationDetailBusy = Boolean(reviewTarget) && (verificationDetailLoading || verificationDetailFetching);

  const payload = isInvestor ? investorRes?.data : ownerRes?.data;
  const profile = payload?.profile;
  const summary = payload?.summary ?? {};
  const documents = payload?.documents ?? {};
  const investmentsBlock = payload?.investments ?? {};

  const loading = isInvestor ? investorLoading : ownerLoading;
  const error = isInvestor ? investorError : ownerError;

  const refetchDetail = isInvestor ? refetchInvestor : refetchOwner;

  const handleToggleActive = async () => {
    if (!profile?._id) return;
    const isActive = String(profile.status || "").toLowerCase() === "active";
    try {
      await changeUserStatus({
        userId: profile._id,
        status: isActive ? "Inactive" : "Active",
      }).unwrap();
      refetchDetail();
    } catch {
      /* toast from base query */
    }
  };

  const openKycDialog = () => {
    const current = String(profile?.kycStatus || "").trim() || "Pending";
    const match = KYC_STATUS_OPTIONS.find((o) => o.toLowerCase() === current.toLowerCase());
    setKycStatusDraft(match || current);
    setKycDialogOpen(true);
  };

  const handleSaveKycStatus = async () => {
    if (!profile?._id) return;
    try {
      await verifyKYC({
        userId: String(profile._id).trim(),
        kycStatus: kycStatusDraft,
      }).unwrap();
      setKycDialogOpen(false);
      refetchDetail();
    } catch {
      /* toast from base query */
    }
  };

  const openVerificationModal = (type) => {
    const qid = verificationDetailQueryId(type, profile);
    if (!qid || !profile?._id) return;
    setReviewTarget({
      type: String(type).toUpperCase(),
      id: qid,
      listUserId: String(profile._id),
    });
  };

  const verificationButtonMeta = React.useMemo(() => {
    if (!profile) return [];
    const rows = [
      { type: "REGA", label: "REGA" },
      { type: "VARA", label: "VARA" },
      { type: "NAFATH", label: "NAFATH" },
      { type: "TAUFEEQ", label: "TAUFEEQ" },
    ];
    return rows
      .map((r) => ({
        ...r,
        detailId: verificationDetailQueryId(r.type, profile),
      }))
      .filter((r) => Boolean(r.detailId));
  }, [profile]);

  const investmentRows = React.useMemo(
    () => (Array.isArray(investmentsBlock.data) ? investmentsBlock.data : []),
    [investmentsBlock.data],
  );
  const filteredInvestments = investmentRows.filter((row) => {
    if (typeFilter !== "all" && String(row.type || "") !== typeFilter) return false;
    if (statusFilter !== "all" && String(row.assetStatus || "") !== statusFilter) return false;
    return true;
  });

  const typeOptions = React.useMemo(() => {
    const s = new Set(investmentRows.map((r) => r.type).filter(Boolean));
    return ["all", ...Array.from(s)];
  }, [investmentRows]);

  const statusOptions = React.useMemo(() => {
    const s = new Set(investmentRows.map((r) => r.assetStatus).filter(Boolean));
    return ["all", ...Array.from(s)];
  }, [investmentRows]);

  const invTotal = Number(investmentsBlock.total);
  const invCount = Number.isFinite(invTotal) ? invTotal : filteredInvestments.length;

  const kycSelectOptions = React.useMemo(() => {
    const current = String(profile?.kycStatus ?? "").trim();
    if (!current) return KYC_STATUS_OPTIONS;
    const known = KYC_STATUS_OPTIONS.some((o) => o.toLowerCase() === current.toLowerCase());
    return known ? KYC_STATUS_OPTIONS : [current, ...KYC_STATUS_OPTIONS];
  }, [profile?.kycStatus]);

  if (!userId) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIosNew sx={{ fontSize: 14 }} />} onClick={() => navigate("/admin/users")} sx={{ textTransform: "none", mb: 2 }}>
          Back
        </Button>
        <Typography color="error">Invalid user.</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIosNew sx={{ fontSize: 14 }} />}
          onClick={() => navigate("/admin/users")}
          sx={{ textTransform: "none", mb: 2, color: "text.primary" }}
        >
          Back to users
        </Button>
        <Typography color="error">Could not load user details.</Typography>
      </Box>
    );
  }

  const displayName = profile.displayName || "—";
  const isActive = String(profile.status || "").toLowerCase() === "active";
  const kycVerified = String(profile.kycStatus || "").toLowerCase() === "verified";

  const docEntries = Object.entries(documents).filter(([, v]) => v && typeof v === "object" && v.url);

  return (
    <Box>
      <Button
        onClick={() => navigate("/admin/users")}
        startIcon={<ArrowBackIosNew sx={{ fontSize: 14 }} />}
        sx={{
          textTransform: "none",
          color: "text.primary",
          mb: 2,
          px: 0,
          "&:hover": { backgroundColor: "transparent" },
        }}
      >
        Back to User Management
      </Button>

      <Typography variant="h5" fontWeight={700} sx={{ color: "text.primary", letterSpacing: -0.5 }}>
        User Details
      </Typography>
      <Typography variant="body2" sx={{ color: "text.muted", mt: 0.5, mb: 3 }}>
        Complete user profile and activity
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.border.light}`,
          boxShadow: (theme) => theme.palette.shadow.statCard,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2.5}
          alignItems={{ xs: "flex-start", md: "flex-start" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
            <Avatar
              src={profile.profileImageUrl || undefined}
              alt=""
              sx={{ width: 72, height: 72, fontSize: 28 }}
            >
              {(displayName || "?")[0]}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 0.5 }}>
                <Typography fontWeight={700} fontSize={20} sx={{ color: "text.primary" }}>
                  {displayName}
                </Typography>
                {kycVerified ? statusBadge("Verified") : null}
                {statusBadge(profile.status || "Inactive")}
              </Stack>
              <Typography variant="body2" sx={{ color: shellAdmin.accent, fontWeight: 600, mb: 1.5 }}>
                {roleLabel(profile.role)}
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" gap={1} sx={{ color: "text.secondary", fontSize: 14 }}>
                  <EmailOutlined sx={{ fontSize: 18, color: "text.muted" }} />
                  <Typography variant="body2" noWrap>
                    {profile.email || "—"}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1} sx={{ color: "text.secondary", fontSize: 14 }}>
                  <PhoneOutlined sx={{ fontSize: 18, color: "text.muted" }} />
                  <Typography variant="body2">{profile.phoneNumber || "—"}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1} sx={{ color: "text.secondary", fontSize: 14 }}>
                  <LocationOnOutlined sx={{ fontSize: 18, color: "text.muted" }} />
                  <Typography variant="body2">{profile.location || "—"}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1} sx={{ color: "text.secondary", fontSize: 14 }}>
                  <CalendarTodayOutlined sx={{ fontSize: 18, color: "text.muted" }} />
                  <Typography variant="body2">Joined {formatJoined(profile.createdAt)}</Typography>
                </Stack>
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
                {profile.kycStatus && !kycVerified ? (
                  <Chip size="small" label={`KYC: ${profile.kycStatus}`} variant="outlined" sx={{ fontSize: 11 }} />
                ) : null}
                {profile.nafathStatus ? (
                  <Chip size="small" label={`Nafath: ${profile.nafathStatus}`} variant="outlined" sx={{ fontSize: 11 }} />
                ) : null}
                {profile.verifiedByVARA != null && profile.verifiedByVARA !== "" ? (
                  <Chip size="small" label={`VARA: ${profile.verifiedByVARA}`} variant="outlined" sx={{ fontSize: 11 }} />
                ) : null}
              </Stack>
            </Box>
          </Stack>

          <Stack spacing={1} alignItems={{ xs: "stretch", sm: "flex-end" }} flexShrink={0}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={openKycDialog}
                disabled={!profile._id || kycSaving}
                sx={{ textTransform: "none", borderRadius: 1.5 }}
              >
                KYC Verification
              </Button>
              {/* <Button
                variant="outlined"
                size="small"
                component={profile.email ? "a" : "button"}
                href={profile.email ? `mailto:${profile.email}` : undefined}
                disabled={!profile.email}
                sx={{ textTransform: "none", borderRadius: 1.5 }}
              >
                Email User
              </Button> */}
              <Button
                variant="outlined"
                size="small"
                onClick={handleToggleActive}
                disabled={statusLoading}
                startIcon={<PauseCircleOutline fontSize="small" />}
                sx={{ textTransform: "none", borderRadius: 1.5 }}
              >
                {isActive ? "Set Inactive" : "Set Active"}
              </Button>
              {verificationButtonMeta.length > 0 ? (
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={1}
                  justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                  sx={{ maxWidth: { sm: 420 } }}
                >
                  {verificationButtonMeta.map(({ type, label }) => (
                    <Button
                      key={type}
                      variant="outlined"
                      size="small"
                      onClick={() => openVerificationModal(type)}
                      sx={{ textTransform: "none", borderRadius: 1.5, fontWeight: 600 }}
                    >
                      {label}
                    </Button>
                  ))}
                </Stack>
              ) : null}
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {isInvestor ? (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                icon={<SarIcon sx={{ color: shellAdmin.accent }} />}
                title="Total Invested"
                value={formatMoney(summary.totalInvestedAmount)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                icon={<TrendingUp sx={{ color: "#16a34a" }} />}
                title="Portfolio Value"
                value={formatMoney(summary.portfolioValue)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                icon={<Apartment sx={{ color: "#7c3aed" }} />}
                title="Active Assets"
                value={summary.activeAssetsCount != null ? String(summary.activeAssetsCount) : "—"}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                icon={<AccountBalanceWallet sx={{ color: "#0d9488" }} />}
                title="Funded Assets"
                value={summary.fundedAssetsCount != null ? String(summary.fundedAssetsCount) : "—"}
              />
            </Grid>
          </>
        ) : (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                icon={<Apartment sx={{ color: shellAdmin.accent }} />}
                title="Assets Created"
                value={summary.totalAssetsCreated != null ? String(summary.totalAssetsCreated) : "—"}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                icon={<SarIcon sx={{ color: "#16a34a" }} />}
                title="Total Assets Value"
                value={formatMoney(summary.totalAssetsValue)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                icon={<TrendingUp sx={{ color: "#7c3aed" }} />}
                title="Active Assets"
                value={summary.activeAssetsCount != null ? String(summary.activeAssetsCount) : "—"}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                icon={<AccountBalanceWallet sx={{ color: "#0d9488" }} />}
                title="Funded Assets"
                value={summary.fundedAssetsCount != null ? String(summary.fundedAssetsCount) : "—"}
              />
            </Grid>
          </>
        )}
      </Grid>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.border.light}`,
          boxShadow: (theme) => theme.palette.shadow.statCard,
          overflow: "hidden",
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 2,
            pt: 1,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
          }}
        >
          {isInvestor ? <Tab label="Investments" /> : null}
          {/* <Tab label="Activity Log" /> */}
          <Tab label="Documents" />
        </Tabs>

        <Box sx={{ px: 2, pb: 2 }}>
          {isInvestor ? (
            <TabPanel value={tab} index={0}>
              <>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Box>
                    <Typography fontWeight={700} sx={{ color: "text.primary" }}>
                      Investment Portfolio
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.muted" }}>
                      All assets owned by this user
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} sx={{ borderRadius: 1 }}>
                        {typeOptions.map((t) => (
                          <MenuItem key={t} value={t}>
                            {t === "all" ? "All Types" : t}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ borderRadius: 1 }}>
                        {statusOptions.map((t) => (
                          <MenuItem key={t} value={t}>
                            {t === "all" ? "All Status" : t}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </Stack>
                <Table size="small">
                  <TableHead sx={{ bgcolor: (theme) => theme.palette.table.headBg }}>
                    <TableRow>
                      {["Asset", "Type", "Invested", "Current Value", "Tokens", "Payment Method", "Transaction ID", "Status"].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, color: (t) => t.palette.table.headText }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredInvestments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                            No investments match the current filters on this page.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredInvestments.map((row) => (
                        <TableRow key={row.investmentId || row.assetId} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{row.assetName || "—"}</TableCell>
                          <TableCell>
                            <Chip label={row.type || "—"} size="small" sx={{ fontSize: 11, fontWeight: 600 }} />
                          </TableCell>
                          <TableCell>{formatMoney(row.investedAmount)}</TableCell>
                          <TableCell>{formatMoney(row.currentValue)}</TableCell>
                          <TableCell>{row.tokens != null ? String(row.tokens) : "—"}</TableCell>
                          <TableCell><TruncatedCell value={row.paymentMethod} /></TableCell>
                          <TableCell><TruncatedCell value={row.transactionId} /></TableCell>
                          <TableCell>{statusBadge(row.assetStatus || "—")}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[5, 10, 25]}
                  count={invCount}
                  rowsPerPage={invRowsPerPage}
                  page={invPage}
                  onPageChange={(_, p) => setInvPage(p)}
                  onRowsPerPageChange={(e) => {
                    setInvRowsPerPage(parseInt(e.target.value, 10));
                    setInvPage(0);
                  }}
                />
              </>
            </TabPanel>
          ) : null}

          {/* Activity Log — re-enable when activity API is wired
          <TabPanel value={tab} index={1}>
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No activity log entries yet.
            </Typography>
          </TabPanel>
          */}

          <TabPanel value={tab} index={documentsTabIndex}>
            {docEntries.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                No documents on file.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {docEntries.map(([key, doc]) => (
                  <Paper
                    key={key}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <DescriptionOutlined color="action" />
                      <Box>
                        <Typography fontWeight={600}>{doc.title || key}</Typography>
                        <Typography variant="caption" color="text.muted">
                          {doc.mimeType || "File"}
                        </Typography>
                      </Box>
                    </Stack>
                    <Button
                      size="small"
                      component={Link}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNew sx={{ fontSize: 16 }} />}
                      sx={{ textTransform: "none" }}
                    >
                      Open
                    </Button>
                  </Paper>
                ))}
              </Stack>
            )}
          </TabPanel>
        </Box>
      </Paper>

      <VerificationReviewDialog
        open={Boolean(reviewTarget)}
        onClose={() => setReviewTarget(null)}
        loading={verificationDetailBusy}
        fetchError={verificationDetailIsError ? verificationDetailErr : null}
        detail={verificationDetailIsError ? null : verificationDetailPayload}
        detailQueryId={reviewTarget?.id}
        listUserId={reviewTarget?.listUserId}
        assetOwnersTab={!isInvestor}
        verificationType={reviewTarget?.type}
        externalStatusHints={{
          NAFATH: profile?.nafathStatus,
          VARA: profile?.verifiedByVARA,
        }}
        onActionSuccess={() => refetchDetail()}
      />

      <Dialog open={kycDialogOpen} onClose={() => !kycSaving && setKycDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Update KYC status</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Set verification status for {displayName}. This updates the user&apos;s KYC record on the server.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="admin-kyc-status-label">KYC status</InputLabel>
            <Select
              labelId="admin-kyc-status-label"
              label="KYC status"
              value={kycStatusDraft}
              onChange={(e) => setKycStatusDraft(e.target.value)}
            >
              {[...new Set([...kycSelectOptions, kycStatusDraft].filter(Boolean))].map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setKycDialogOpen(false)} disabled={kycSaving} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveKycStatus}
            disabled={kycSaving}
            sx={{ textTransform: "none" }}
          >
            {kycSaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
